import { Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth/AuthContext";
import { LoginPage } from "./lib/auth/LoginPage";
import { UserReport } from "./apps/userreport/UserReport";

// The authenticated shell (hub, product views, AG Grid, …) is lazy so it stays
// OUT of the entry chunk. The public /r/:token report route below therefore
// downloads only React + the tiny report code — no ~1MB AG Grid on cellular.
const Home = lazy(() => import("./Home"));
// Public live demo (/demo/:project), framed by the marketing sites. Its own
// chunk: no auth shell, no grid, no charts.
const DemoPage = lazy(() => import("./apps/demo/DemoPage"));

// Auth gate: when a Cognito pool is configured (deployed envs), require sign-in;
// locally without a pool the app runs open against sample data.
function RequireAuth({ children }: { children: JSX.Element }) {
  const { status } = useAuth();
  if (status === "disabled") return children;
  if (status === "loading") return <div className="sn-meta sn-app">Checking session…</div>;
  if (status === "signedIn") return children;
  return <LoginPage />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public, login-free end-user report (SMS-delivered snapshot by token). */}
        <Route path="/r/:token" element={<UserReport />} />
        {/* Public, login-free live demo; ?embed=1 for marketing-site iframes. */}
        <Route
          path="/demo/:project"
          element={
            <Suspense fallback={<div className="sn-demo-wrap sn-demo-msg">Loading demo…</div>}>
              <DemoPage />
            </Suspense>
          }
        />
        <Route
          path="/demo/*"
          element={<div className="sn-demo-wrap sn-demo-msg">This demo isn't available</div>}
        />
        <Route
          path="*"
          element={
            <RequireAuth>
              <Suspense fallback={<div className="sn-meta sn-app">Loading…</div>}>
                <Home />
              </Suspense>
            </RequireAuth>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
